/**
 * The templates list (docs/ux/templates.md): the report types that produce
 * a document with their base's live version, section count, overlay count
 * and last change; the packs beneath; consultation advice named as producing
 * no report. Read only here; the editors are a click away.
 */

import { reportTypeLabel, type ReportTypeKind } from '@beam/core/status';
import { Link } from 'react-router';
import { useOnline } from '@/app/online';
import { useIsPhone } from '@/app/viewport';
import { Legend, LegendItem } from '@/components/shell/Legend';
import { PageHeader } from '@/components/shell/PageHeader';
import { useShellWidth } from '@/components/shell/context';
import { ErrorPanel } from '@/components/shell/states';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { shortDate } from '@/lib/format';
import { TYPES } from '../describe';
import { RegistryPill } from '../RegistryPill';
import { FormsPanel } from './FormsPanel';
import { useTemplatesIndex } from './useTemplates';

export function TemplatesPage() {
  useShellWidth('narrow');
  const phone = useIsPhone();
  const online = useOnline();
  const index = useTemplatesIndex();
  const header = <PageHeader title="Templates" description="The structure every generated report follows: a base per report type, the psychiatry pack, and each doctor's overlay." tabs={<RegistryPill current="templates" />} />;
  if (index.isError) return <div className="flex flex-col gap-3">{header}<ErrorPanel message="The templates could not be loaded." onRetry={() => void index.refetch()} /></div>;
  if (index.isPending) return <div className="flex flex-col gap-3">{header}<Skeleton className="h-64 rounded-xl" aria-label="Loading templates" /></div>;
  const d = index.data;
  const rows = TYPES.map((t: ReportTypeKind) => {
    const base = d.bases.find((b) => b.head.id === t);
    return { t, base, overlays: d.overlays.filter((o) => o.reportType === t).length };
  });

  return (
    <div className="flex flex-col gap-3">
      {header}
      {phone ? (
        <ul className="flex flex-col gap-2" data-slot="template-cards">
          {rows.map(({ t, base, overlays }) => (
            <li key={t} data-slot="template-card" data-type={t} className="flex flex-col gap-1 rounded-xl border border-beam-line bg-beam-paper p-4 text-sm">
              <span className="font-medium">{reportTypeLabel(t, 'label')}</span>
              {base ? <span className="text-beam-text-muted"><span className="tabular">Version {base.head.liveVersion}</span> live, {base.sections} sections, {overlays} {overlays === 1 ? 'overlay' : 'overlays'}</span> : <span className="text-beam-text-muted">Produces no report.</span>}
              {base ? <Link to={`/registry/templates/${t}`} className={buttonVariants({ variant: 'outline', size: 'sm' }) + ' mt-1 self-start'} aria-label={`Open the ${reportTypeLabel(t, 'label')} base`}>Open</Link> : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-beam-line bg-beam-paper">
          <Table data-slot="template-table" className="text-[13px]">
            <caption className="sr-only">Report type bases and their live versions</caption>
            <TableHeader className="bg-beam-cream-deep">
              <TableRow className="border-beam-line hover:bg-beam-cream-deep">
                <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Report type</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[80px] px-4 text-beam-text-soft">Live</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[96px] px-4 text-right text-beam-text-soft">Sections</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[96px] px-4 text-right text-beam-text-soft">Overlays</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[170px] px-4 text-beam-text-soft">Changed</TableHead>
                <TableHead scope="col" className="h-9 w-[90px] px-4"><span className="sr-only">Open</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ t, base, overlays }) => (
                <TableRow key={t} data-slot="base-row" data-type={t} className={`h-[46px] border-beam-line ${base ? 'hover:bg-beam-cream-deep' : 'text-beam-text-muted hover:bg-transparent'}`}>
                  <TableCell className="px-4 py-0 font-medium">{reportTypeLabel(t, 'label')}</TableCell>
                  <TableCell className="tabular px-4 py-0" data-slot="live-version">{base ? `v${base.head.liveVersion}` : ''}</TableCell>
                  <TableCell className="tabular px-4 py-0 text-right">{base ? base.sections : ''}</TableCell>
                  <TableCell className="tabular px-4 py-0 text-right">{base ? overlays : ''}</TableCell>
                  <TableCell className="px-4 py-0">{base ? `${shortDate(base.head.updatedAt)}, ${base.head.updatedByName ?? base.head.updatedBy}` : 'Produces no report'}</TableCell>
                  <TableCell className="px-4 py-0 text-right">{base ? <Link to={`/registry/templates/${t}`} className={buttonVariants({ variant: 'outline', size: 'default' })} aria-label={`Open the ${reportTypeLabel(t, 'label')} base`}>Open</Link> : null}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <section aria-label="Packs" className="rounded-xl border border-beam-line bg-beam-paper p-4">
        <h2 className="eyebrow mb-2 text-beam-text-muted">Packs</h2>
        <ul className="flex flex-col divide-y divide-beam-line text-sm" data-slot="pack-list">
          {d.packs.map((p) => (
            <li key={p.head.id} data-pack={p.head.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
              <span className="font-medium">{p.head.label}</span>
              <span className="text-beam-text-muted"><span className="tabular">v{p.head.liveVersion}</span>, {p.adds} added, {p.removes} removed; applied to psychiatrists' overlays</span>
            </li>
          ))}
        </ul>
      </section>
      <FormsPanel offline={!online} />
      <Legend>
        <LegendItem term={<span className="font-medium">Live</span>}>the version the workspace and the composer read; every save adds a version and moves the pointer.</LegendItem>
        <LegendItem term={<span className="font-medium">Overlays</span>}>doctors whose structure differs from the base; edit one from the doctor's page.</LegendItem>
        <LegendItem term={<span className="font-medium">Statutory forms</span>}>filled, never authored: the permanent impairment base's cells are the certificate's, and the official files below are what they fill.</LegendItem>
      </Legend>
    </div>
  );
}
